/**
 * Nomination workflow service — status transition logic
 * Requirements: 2.2, 2.5, 3.3, 3.4, 4.1, 4.3
 */

import {
  Nomination,
  NominationStatus,
  CreateNominationInput,
} from '../types/nomination';
import { validateCreateNominationInput } from '../types/nominationValidation';

/**
 * Result of a workflow operation.
 */
export interface NominationWorkflowResult {
  success: boolean;
  nomination?: Nomination;
  errors: string[];
}

/**
 * Valid status transitions per the design state machine.
 *
 *   (new)         → Draft          (Manager saves)
 *   Draft         → Submitted      (Manager submits)
 *   Submitted     → Under Review   (Reviewer picks up)
 *   Submitted     → Rejected       (Reviewer rejects)
 *   Under Review  → Approved       (Reviewer approves)
 *   Under Review  → Rejected       (Reviewer rejects)
 *   Approved      → Published      (Reviewer publishes)
 *   Rejected      → Draft          (Manager re-edits)
 */
export const VALID_TRANSITIONS: Record<NominationStatus, NominationStatus[]> = {
  [NominationStatus.DRAFT]: [NominationStatus.SUBMITTED],
  [NominationStatus.SUBMITTED]: [
    NominationStatus.UNDER_REVIEW,
    NominationStatus.REJECTED,
  ],
  [NominationStatus.UNDER_REVIEW]: [
    NominationStatus.APPROVED,
    NominationStatus.REJECTED,
  ],
  [NominationStatus.APPROVED]: [NominationStatus.PUBLISHED],
  [NominationStatus.REJECTED]: [NominationStatus.DRAFT],
  [NominationStatus.PUBLISHED]: [],
};

/**
 * Check whether a status transition is allowed.
 */
export function isValidTransition(
  from: NominationStatus,
  to: NominationStatus
): boolean {
  return (VALID_TRANSITIONS[from] ?? []).includes(to);
}

// ---- helpers ----

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function now(): string {
  return new Date().toISOString();
}

function cloneNomination(n: Nomination, overrides: Partial<Nomination>): Nomination {
  return { ...n, ...overrides, updatedAt: now() };
}

// ---- public API ----

/**
 * Save a new nomination as Draft.
 * Requirements: 2.5
 */
export function saveDraft(
  input: CreateNominationInput,
  nominatorId: string,
  nominatorName: string
): NominationWorkflowResult {
  const validation = validateCreateNominationInput(input);
  if (!validation.isValid) {
    return { success: false, errors: validation.errors };
  }

  const ts = now();
  const nomination: Nomination = {
    id: generateId(),
    nomineeId: input.nomineeId,
    nomineeName: input.nomineeName,
    nominatorId,
    nominatorName,
    awardType: input.awardType,
    reason: input.reason,
    supportingDetails: input.supportingDetails,
    status: NominationStatus.DRAFT,
    reviewerNotes: [],
    createdAt: ts,
    updatedAt: ts,
  };

  return { success: true, nomination, errors: [] };
}

/**
 * Create and immediately submit a nomination (status = Submitted).
 * Requirements: 2.2
 */
export function submitNomination(
  input: CreateNominationInput,
  nominatorId: string,
  nominatorName: string
): NominationWorkflowResult {
  const validation = validateCreateNominationInput(input);
  if (!validation.isValid) {
    return { success: false, errors: validation.errors };
  }

  const ts = now();
  const nomination: Nomination = {
    id: generateId(),
    nomineeId: input.nomineeId,
    nomineeName: input.nomineeName,
    nominatorId,
    nominatorName,
    awardType: input.awardType,
    reason: input.reason,
    supportingDetails: input.supportingDetails,
    status: NominationStatus.SUBMITTED,
    reviewerNotes: [],
    createdAt: ts,
    updatedAt: ts,
  };

  return { success: true, nomination, errors: [] };
}

/**
 * Approve a nomination (Submitted | Under Review → Approved).
 * Requirements: 3.3
 */
export function approveNomination(
  nomination: Nomination
): NominationWorkflowResult {
  // Approving from Submitted implicitly moves through Under Review
  if (
    nomination.status !== NominationStatus.SUBMITTED &&
    nomination.status !== NominationStatus.UNDER_REVIEW
  ) {
    return {
      success: false,
      errors: [
        `Cannot approve a nomination with status "${nomination.status}". Only "Submitted" or "Under Review" nominations can be approved.`,
      ],
    };
  }

  return {
    success: true,
    nomination: cloneNomination(nomination, {
      status: NominationStatus.APPROVED,
    }),
    errors: [],
  };
}

/**
 * Reject a nomination (Submitted | Under Review → Rejected).
 * A rejection reason is required.
 * Requirements: 3.4
 */
export function rejectNomination(
  nomination: Nomination,
  rejectionReason?: string
): NominationWorkflowResult {
  if (
    nomination.status !== NominationStatus.SUBMITTED &&
    nomination.status !== NominationStatus.UNDER_REVIEW
  ) {
    return {
      success: false,
      errors: [
        `Cannot reject a nomination with status "${nomination.status}". Only "Submitted" or "Under Review" nominations can be rejected.`,
      ],
    };
  }

  if (!rejectionReason || rejectionReason.trim().length === 0) {
    return {
      success: false,
      errors: ['A rejection reason is required'],
    };
  }

  return {
    success: true,
    nomination: cloneNomination(nomination, {
      status: NominationStatus.REJECTED,
    }),
    errors: [],
  };
}

/**
 * Publish an approved nomination (Approved → Published).
 * Only nominations with "Approved" status can be published.
 * Requirements: 4.1, 4.3
 */
export function publishNomination(
  nomination: Nomination,
  publisherId: string
): NominationWorkflowResult {
  if (nomination.status !== NominationStatus.APPROVED) {
    return {
      success: false,
      errors: [
        `Cannot publish a nomination with status "${nomination.status}". Only "Approved" nominations can be published.`,
      ],
    };
  }

  const ts = now();
  return {
    success: true,
    nomination: cloneNomination(nomination, {
      status: NominationStatus.PUBLISHED,
      publishedAt: ts,
      publishedBy: publisherId,
    }),
    errors: [],
  };
}
